import type { FunctionComponent } from "preact";
import { mapSpeedToAngle } from "./calc";

interface Props {
  displayedSpeedKmh: number;
  referenceSpeedKmh: number;
  toleranceMinPercent: number;
  toleranceMaxPercent: number;
  withinTolerance: boolean;
}

const SPEEDO_MIN = 0;
const SPEEDO_MAX = 200;
const TICK_STEP = 20;
const CX = 120;
const CY = 130;
const R_OUTER = 100;
const R_TICK_INNER = 88;
const R_TICK_LABEL = 76;
const R_NEEDLE = 92;
const R_HUB = 6;
const R_TOL = 96;
const TOL_STROKE = 8;

const polar = (
  cx: number,
  cy: number,
  r: number,
  angleDeg: number,
): { x: number; y: number } => {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.sin(rad), y: cy - r * Math.cos(rad) };
};

const formatSpeed = (value: number): string =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(
    value,
  );

const Speedometer: FunctionComponent<Props> = ({
  displayedSpeedKmh,
  referenceSpeedKmh,
  toleranceMinPercent,
  toleranceMaxPercent,
  withinTolerance,
}) => {
  const lowerToleranceSpeed = referenceSpeedKmh / (1 + toleranceMaxPercent / 100);
  const upperToleranceSpeed = referenceSpeedKmh / (1 + toleranceMinPercent / 100);
  const tolStartAngle = mapSpeedToAngle(lowerToleranceSpeed, SPEEDO_MIN, SPEEDO_MAX);
  const tolEndAngle = mapSpeedToAngle(upperToleranceSpeed, SPEEDO_MIN, SPEEDO_MAX);
  const tolStart = polar(CX, CY, R_TOL, tolStartAngle);
  const tolEnd = polar(CX, CY, R_TOL, tolEndAngle);
  const largeArcFlag = tolEndAngle - tolStartAngle > 180 ? 1 : 0;
  const tolerancePath = [
    "M",
    tolStart.x,
    tolStart.y,
    "A",
    R_TOL,
    R_TOL,
    0,
    largeArcFlag,
    1,
    tolEnd.x,
    tolEnd.y,
  ].join(" ");

  const dialStart = polar(CX, CY, R_OUTER, -90);
  const dialEnd = polar(CX, CY, R_OUTER, 90);
  const dialPath = [
    "M",
    dialStart.x,
    dialStart.y,
    "A",
    R_OUTER,
    R_OUTER,
    0,
    0,
    1,
    dialEnd.x,
    dialEnd.y,
  ].join(" ");

  const ticks: { x1: number; y1: number; x2: number; y2: number; lx: number; ly: number; label: string }[] =
    [];
  for (let s = SPEEDO_MIN; s <= SPEEDO_MAX; s += TICK_STEP) {
    const angle = mapSpeedToAngle(s, SPEEDO_MIN, SPEEDO_MAX);
    const outer = polar(CX, CY, R_OUTER, angle);
    const inner = polar(CX, CY, R_TICK_INNER, angle);
    const label = polar(CX, CY, R_TICK_LABEL, angle);
    ticks.push({ x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y, lx: label.x, ly: label.y, label: String(s) });
  }

  const needleAngle = mapSpeedToAngle(displayedSpeedKmh, SPEEDO_MIN, SPEEDO_MAX);
  const needleTip = polar(CX, CY, R_NEEDLE, needleAngle);
  const needleClass = `speedo-needle${withinTolerance ? "" : " speedo-needle--off"}`;

  return (
    <div class="speedo">
      <svg
        class="speedo-svg"
        viewBox="0 0 240 170"
        role="img"
        aria-label={`Tacho zeigt ${formatSpeed(displayedSpeedKmh)} km/h`}
      >
        <path class="speedo-arc" d={dialPath} />
        <path class="speedo-arc-tolerance" d={tolerancePath} stroke-width={TOL_STROKE} />
        {ticks.map((t) => (
          <g key={t.label}>
            <line class="speedo-tick" x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} />
            <text class="speedo-tick-label" x={t.lx} y={t.ly} text-anchor="middle" dominant-baseline="middle">
              {t.label}
            </text>
          </g>
        ))}
        <line
          class={needleClass}
          x1={CX}
          y1={CY}
          x2={needleTip.x}
          y2={needleTip.y}
          style={{ transformOrigin: `${String(CX)}px ${String(CY)}px` }}
        />
        <circle class="speedo-hub" cx={CX} cy={CY} r={R_HUB} />
      </svg>
      <div class="speedo-readout">
        <span class="speedo-readout-value">{formatSpeed(displayedSpeedKmh)} km/h</span>
        <span class="speedo-readout-meta">
          tatsächlich {formatSpeed(referenceSpeedKmh)} km/h
        </span>
      </div>
    </div>
  );
};

export default Speedometer;
